import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isBefore } from "date-fns";
import { useEffect } from "react";
import { useParams } from "react-router";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { isDefined } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { useSurveyQueries } from "@/queries/surveys/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { SurveyActionSection } from "./components/SurveyActionSection";
import { SurveyExportModal } from "./components/SurveyExportModal";
import { SurveyInfoSection } from "./components/SurveyInfoSection";
import { SurveyQuestionResponseCard } from "./components/SurveyQuestionResponseCard";

export function SurveyDetailPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  const [isSurveyExportModalOpened, surveyExportModalOpenHandlers] = useBool();
  const { id: surveyId } = useParams<{ id: string }>();

  const surveyAiFollowUpActions = useConfig("surveyAiFollowUpSuggestions");
  const queryClient = useQueryClient();
  const surveyQueries = useSurveyQueries();
  const {
    data: survey,
    error: errorSurveyDetails,
    isPending: isPendingSurveyDetails,
  } = useQuery({
    ...surveyQueries.details({ surveyId: surveyId!, category: "other" }),
    enabled: !!surveyId,
  });
  const projectId = useProjectId();
  const slug = useSlug();
  const { t } = useTranslation();

  const isFinished = !!survey?.endsAt && isBefore(survey.endsAt, clockDate);
  const canExport = isDefined(survey?.answerCount) && survey?.answerCount > 0;

  useEffect(() => {
    if (isFinished && survey?.canViewResults && surveyAiFollowUpActions) {
      void queryClient.prefetchQuery(surveyQueries.suggestedFollowUps(survey.id));
    }
  }, [isFinished, survey?.canViewResults, survey?.id, queryClient, surveyQueries, surveyAiFollowUpActions]);

  if (isPendingSurveyDetails) {
    return <FullSizeLoader withPadding />;
  }

  if (errorSurveyDetails) {
    return <ErrorPage error={errorSurveyDetails} />;
  }

  const allQuestions = survey.questionGroups.flatMap((group) => group.questions);

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={survey.title.trim() || t("page.survey-details.survey-title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.surveys.list.title"),
                to: routes.surveys.list({ slug }),
              },
              {
                name: t("page.survey-details.survey-title"),
              },
            ]}
          />
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{ scope: "project", entity: "survey", page: "detail", entityId: survey.id }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.survey-detail.suggestion.1"),
                t("component.ask-nobi-chat-drawer.survey-detail.suggestion.2"),
              ]}
            />
            <Button
              icon={<Download01Icon />}
              onClick={surveyExportModalOpenHandlers.setTrue}
              data-testid="survey-export-btn"
              variant="soft"
              title={canExport ? undefined : t("page.survey-deteails.export-btn.tooltip.disabled")}
              disabled={!canExport}
            >
              {t("common.action.export")}
            </Button>
          </>
        }
      >
        <PageGrid.Row>
          <PageGrid.Column size="66%" isDesktopIndifferent>
            <Stack gap="md">
              <SurveyInfoSection showDuration showStatus {...{ survey }} />

              {/* Mobile */}
              {isFinished && (
                <aside className="block @md:hidden">
                  <SurveyActionSection survey={survey} />
                </aside>
              )}

              <Stack gap="md">
                {allQuestions.map((question) => (
                  <SurveyQuestionResponseCard
                    key={question.id}
                    canViewResults={survey.canViewResults}
                    isSurveyEnded={isFinished}
                    {...{ question }}
                  />
                ))}
              </Stack>
            </Stack>
          </PageGrid.Column>
          {/* Desktop */}
          {isFinished && (
            <PageGrid.Column size="33%" isDesktopIndifferent>
              <aside className="hidden @md:block">
                <SurveyActionSection survey={survey} />
              </aside>
            </PageGrid.Column>
          )}
        </PageGrid.Row>
      </DocumentPage>
      <SurveyExportModal
        isOpened={isSurveyExportModalOpened}
        onOpenChange={surveyExportModalOpenHandlers.set}
        survey={survey}
      />
    </>
  );
}
